import PlanningAreas from './PlanningAreas';
import {pageDefinitions,sidebarSections} from './app/navigation';
import React, { useEffect, useState } from 'react';
import Gates from './Gates';
import LoadingBays from './LoadingBays';
import Vehicles from './Vehicles';
import Drivers from './Drivers';
import VehicleClasses from './VehicleClasses';
import GeneralSettings from './GeneralSettings';
import WebsiteUsers from './WebsiteUsers';
import JobPlanner from './JobPlanner';
import Jobs from './Jobs';
import LicenseView from './LicenseView';
import GateDisplay from './GateDisplay';
import LayoutEditor from './LayoutEditor';
import LayoutViewer from './LayoutViewer';
import WarehouseLookup from './WarehouseLookup';
import DisplayLayoutEditor from './DisplayLayoutEditor';
import { API_BASE, apiFetch, clearStoredUser, getStoredUser, showAppConfirm, storeUser } from './api';
import {
  applyThemeVariables,
  brandedPageStyle,
  mergeStyles,
  theme,
  THEME_MODE_DARK,
  THEME_MODE_LIGHT,
} from './theme';

const APP_NAME = 'DispoBoard';
const DEFAULT_FAVICON = `${process.env.PUBLIC_URL || ''}/favicon.ico`;
const THEME_STORAGE_PREFIX = 'dispoboard_theme_mode';
const SIDEBAR_STORAGE_PREFIX = 'dispoboard_sidebar_collapsed';
const SIDEBAR_EXPANDED_WIDTH = 236;
const SIDEBAR_COLLAPSED_WIDTH = 84;

const MOBILE_TAB_PAGE_KEYS = ['overview', 'planner', 'vehicles'];
const MOBILE_RESTRICTED_PAGE_KEYS = new Set(['displayLayout', 'layoutEditor', 'warehouseLookup']);

const styles = {
  app: {
    ...theme.page,
    overflowX: 'hidden',
  },
  displayLicenseInactive: {
    width: '100vw',
    height: '100vh',
    margin: 0,
    display: 'grid',
    placeContent: 'center',
    gap: '12px',
    background: '#000000',
    color: '#ffffff',
    fontFamily: theme.fontStack,
    textAlign: 'center',
    fontSize: 'clamp(22px, 4vw, 44px)',
    fontWeight: 800,
  },
  shell: {
    ...theme.shell,
    minHeight: '100vh',
  },
  appGrid: {
    display: 'grid',
    gridTemplateColumns: `${SIDEBAR_EXPANDED_WIDTH}px minmax(0, 1fr)`,
    gap: '16px',
    minHeight: 'calc(100vh - 36px)',
  },
  sidebar: {
    ...theme.glassCard,
    padding: '14px 10px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    position: 'sticky',
    top: '18px',
    alignSelf: 'start',
    minHeight: 'calc(100vh - 36px)',
    overflow: 'visible',
  },
  sidebarCollapsed: {
    padding: '14px 8px 12px',
  },
  sidebarTop: {
    display: 'grid',
    gap: '14px',
    padding: '4px 6px 0',
    position: 'relative',
  },
  sidebarTopRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minHeight: '48px',
  },
  sidebarTopRowCollapsed: {
    justifyContent: 'center',
    paddingRight: 0,
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minWidth: 0,
  },
  brandCollapsed: {
    justifyContent: 'center',
    gap: 0,
  },
  brandLogo: {
    width: '48px',
    height: '48px',
    borderRadius: '14px',
    objectFit: 'cover',
    border: '1px solid var(--board-border, rgba(148,163,184,0.16))',
    background: 'var(--board-surface-soft, rgba(255,255,255,0.04))',
    flex: '0 0 auto',
  },
  brandFallback: {
    width: '48px',
    height: '48px',
    borderRadius: '14px',
    display: 'grid',
    placeItems: 'center',
    background: 'var(--board-accent-gradient-soft, linear-gradient(135deg, rgba(59,130,246,0.26) 0%, rgba(99,102,241,0.26) 100%))',
    border: '1px solid var(--board-accent-border, rgba(96,165,250,0.24))',
    color: 'var(--board-text-strong, #f8fafc)',
    fontWeight: 800,
    letterSpacing: '0.06em',
    flex: '0 0 auto',
  },
  brandCopy: {
    display: 'grid',
    gap: '2px',
    minWidth: 0,
  },
  brandCopyCollapsed: {
    display: 'none',
  },
  brandTitle: {
    fontSize: '25px',
    lineHeight: 1,
    fontWeight: 750,
    letterSpacing: '-0.04em',
    color: 'var(--board-text-strong, #f8fafc)',
  },
  brandBoard: {
    fontSize: '11px',
    color: 'var(--board-text-faint, rgba(226,232,240,0.48))',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  sidebarDivider: {
    height: '1px',
    background: 'var(--board-border, rgba(148,163,184,0.14))',
    margin: '0 6px',
  },
  navScroll: {
    display: 'grid',
    gap: '18px',
    alignContent: 'start',
    overflowY: 'auto',
    padding: '0 6px',
    flex: 1,
  },
  navGroup: {
    display: 'grid',
    gap: '4px',
  },
  navGroupCollapsed: {
    justifyItems: 'center',
  },
  navGroupTitle: {
    margin: 0,
    fontSize: '11px',
    fontWeight: 800,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: 'var(--board-text-faint, rgba(226,232,240,0.42))',
    padding: '0 10px',
  },
  navGroupTitleCollapsed: {
    display: 'none',
  },
  navButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    width: '100%',
    padding: '9px 10px',
    borderRadius: '8px',
    border: '1px solid transparent',
    background: 'transparent',
    color: 'var(--board-text-muted, rgba(226,232,240,0.70))',
    cursor: 'pointer',
    textAlign: 'left',
    boxShadow: 'none',
  },
  navButtonCollapsed: {
    justifyContent: 'center',
    padding: '10px 8px',
  },
  navButtonHover: {
    background: 'var(--board-surface-soft, rgba(255,255,255,0.055))',
    border: '1px solid var(--board-border, rgba(148,163,184,0.12))',
    color: 'var(--board-text-strong, #f8fafc)',
  },
  navButtonActive: {
    background: 'var(--board-surface-soft, rgba(255,255,255,0.04))',
    border: '1px solid var(--board-border, rgba(148,163,184,0.14))',
    color: 'var(--board-text-strong, #f8fafc)',
  },
  navShort: {
    width: '20px',
    height: '20px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '15px',
    fontWeight: 700,
    color: 'currentColor',
    flex: '0 0 auto',
    lineHeight: 1,
  },
  navIconGlyph: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '1em',
    height: '1em',
    lineHeight: 1,
  },
  navText: {
    display: 'grid',
    gap: '2px',
    minWidth: 0,
    flex: 1,
  },
  navTextCollapsed: {
    display: 'none',
  },
  navLabel: {
    fontSize: '13px',
    fontWeight: 650,
    color: 'inherit',
  },
  navHint: {
    fontSize: '10px',
    color: 'var(--board-text-faint, rgba(226,232,240,0.40))',
  },
  sidebarFooter: {
    display: 'grid',
    gap: '10px',
    padding: '6px',
  },
  footerButton: {
    ...theme.button,
    ...theme.mutedButton,
    width: '100%',
    justifyContent: 'flex-start',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    boxShadow: 'none',
  },
  footerButtonCollapsed: {
    justifyContent: 'center',
    padding: '9px 8px',
  },
  footerDangerButton: {
    ...theme.button,
    ...theme.dangerButton,
    width: '100%',
    justifyContent: 'flex-start',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    boxShadow: 'none',
  },
  footerDangerButtonCollapsed: {
    justifyContent: 'center',
    padding: '9px 8px',
  },
  footerIcon: {
    width: '20px',
    height: '20px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '15px',
    fontWeight: 700,
    lineHeight: 1,
    color: 'currentColor',
  },
  sidebarToggleButton: {
    width: '36px',
    height: '36px',
    padding: 0,
    display: 'grid',
    placeItems: 'center',
    position: 'absolute',
    top: '10px',
    right: '-18px',
    zIndex: 4,
    borderRadius: '11px',
    border: '1px solid var(--board-accent-border, rgba(96,165,250,0.28))',
    background: 'linear-gradient(180deg, rgba(37,99,235,0.94) 0%, rgba(30,64,175,0.94) 100%)',
    color: '#eff6ff',
    cursor: 'pointer',
    boxShadow: '0 14px 28px rgba(15,23,42,0.28)',
  },
  main: {
    display: 'grid',
    gap: '18px',
    minWidth: 0,
    alignContent: 'start',
  },
  topbar: {
    padding: '2px 2px 6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    minWidth: 0,
    borderBottom: '1px solid var(--board-border, rgba(148,163,184,0.10))',
  },
  topbarLeft: {
    display: 'grid',
    gap: '4px',
    minWidth: 0,
  },
  topbarEyebrow: {
    margin: 0,
    fontSize: '11px',
    fontWeight: 800,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: 'var(--board-text-faint, rgba(226,232,240,0.44))',
  },
  topbarTitle: {
    margin: 0,
    fontSize: '16px',
    fontWeight: 700,
    color: 'var(--board-text-strong, #f8fafc)',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  topbarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
  topbarChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '0',
    minHeight: 'auto',
    background: 'transparent',
    border: 'none',
    color: 'var(--board-text-muted, rgba(226,232,240,0.64))',
  },
  chipLabel: {
    color: 'var(--board-text-faint, rgba(226,232,240,0.38))',
    fontSize: '11px',
    fontWeight: 600,
  },
  chipValue: {
    color: 'var(--board-text-soft, rgba(226,232,240,0.74))',
    fontSize: '12px',
    fontWeight: 600,
  },
  themeToggle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px',
    borderRadius: '999px',
    background: 'var(--board-surface-soft, rgba(255,255,255,0.05))',
    border: '1px solid var(--board-border, rgba(148,163,184,0.14))',
  },
  themeToggleButton: {
    ...theme.button,
    ...theme.mutedButton,
    minWidth: '46px',
    padding: '7px 10px',
    borderRadius: '999px',
    fontSize: '12px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  themeToggleButtonActive: {
    ...theme.primaryButton,
  },
  mobileMenuButton: {
    ...theme.button,
    ...theme.mutedButton,
    width: '40px',
    height: '40px',
    padding: 0,
    display: 'grid',
    placeItems: 'center',
  },
  userButton: {
    ...theme.button,
    ...theme.mutedButton,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 8px',
    position: 'relative',
  },
  userAvatar: {
    width: '24px',
    height: '24px',
    borderRadius: '999px',
    display: 'grid',
    placeItems: 'center',
    background: 'var(--board-surface-soft, rgba(148,163,184,0.14))',
    border: '1px solid var(--board-border, rgba(148,163,184,0.10))',
    color: 'var(--board-text-strong, #f8fafc)',
    fontWeight: 700,
    fontSize: '11px',
    textTransform: 'uppercase',
  },
  userCopy: {
    display: 'grid',
    gap: '1px',
    textAlign: 'left',
    minWidth: 0,
  },
  userName: {
    fontSize: '12px',
    fontWeight: 650,
    color: 'var(--board-text-strong, #f8fafc)',
    whiteSpace: 'nowrap',
  },
  userRole: {
    fontSize: '10px',
    color: 'var(--board-text-faint, rgba(226,232,240,0.46))',
    whiteSpace: 'nowrap',
  },
  userMenu: {
    ...theme.glassCard,
    position: 'absolute',
    right: 0,
    top: 'calc(100% + 10px)',
    minWidth: '190px',
    padding: '10px',
    display: 'grid',
    gap: '8px',
    zIndex: 80,
  },
  userMenuButton: {
    ...theme.button,
    ...theme.mutedButton,
    width: '100%',
    boxShadow: 'none',
  },
  pageHeader: {
    padding: '4px 0 2px',
    display: 'grid',
    gap: '10px',
  },
  pageHeaderTop: {
    display: 'flex',
    justifyContent: 'flex-start',
    gap: '14px',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },
  pageHeading: theme.pageHeading,
  pageEyebrow: theme.pageEyebrow,
  pageTitle: theme.pageTitle,
  pageSubtitle: theme.pageSubtitle,
  content: {
    display: 'grid',
    gap: '18px',
    minWidth: 0,
  },
  card: {
    ...theme.glassCard,
    padding: '22px 24px',
    overflow: 'hidden',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap',
    marginBottom: '16px',
  },
  cardTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 800,
    color: 'var(--board-text-strong, #f8fafc)',
  },
  cardMeta: {
    color: 'var(--board-text-faint, rgba(226,232,240,0.52))',
    fontSize: '13px',
  },
  tableWrap: {
    overflowX: 'auto',
  },
  table: theme.table,
  th: theme.th,
  td: theme.td,
  badge: {
    ...theme.chip,
    padding: '5px 9px',
    borderRadius: '999px',
    background: 'var(--board-accent-soft, rgba(59,130,246,0.16))',
    border: '1px solid var(--board-accent-border, rgba(96,165,250,0.20))',
    fontSize: '11px',
  },
  mutedText: {
    color: 'var(--board-text-muted, rgba(226,232,240,0.56))',
    fontSize: '13px',
  },
  overviewGrid: {
    display: 'grid',
    gap: '16px',
    gridTemplateColumns: 'minmax(0, 1.75fr) minmax(260px, 0.85fr)',
    alignItems: 'start',
  },
  overviewSideStack: {
    display: 'grid',
    gap: '18px',
    alignContent: 'start',
  },
  overviewList: {
    display: 'grid',
    gap: '12px',
  },
  overviewListItem: {
    display: 'grid',
    gap: '5px',
    padding: '12px 0',
    borderBottom: '1px solid var(--board-border, rgba(148,163,184,0.10))',
  },
  deniedCard: {
    ...theme.glassCard,
    padding: '28px',
    color: 'var(--board-text-strong, #f8fafc)',
    fontSize: '20px',
    fontWeight: 800,
  },
  sessionBlockedCard: {
    ...theme.glassCard,
    padding: '32px',
    display: 'grid',
    gap: '16px',
    justifyItems: 'start',
  },
  sessionBlockedTitle: {
    margin: 0,
    fontSize: '22px',
    fontWeight: 800,
    color: 'var(--board-text-strong, #f8fafc)',
  },
  sessionBlockedText: {
    margin: 0,
    maxWidth: '560px',
    color: 'var(--board-text-muted, rgba(226,232,240,0.72))',
    lineHeight: 1.6,
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'var(--board-overlay, rgba(3, 6, 14, 0.72))',
    zIndex: 100,
    backdropFilter: 'blur(8px)',
  },
  drawer: {
    ...theme.glassCard,
    position: 'fixed',
    top: 12,
    left: 12,
    bottom: 12,
    width: '320px',
    maxWidth: 'calc(100vw - 24px)',
    zIndex: 101,
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  mobileTopbar: {
    display: 'grid',
    gridTemplateColumns: '40px minmax(0, 1fr) auto',
    alignItems: 'center',
    gap: '10px',
    padding: '4px 0 6px',
    borderBottom: '1px solid var(--board-border, rgba(148,163,184,0.10))',
  },
  mobileTopbarTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 800,
    color: 'var(--board-text-strong, #f8fafc)',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  mobileBottomNav: {
    position: 'fixed',
    left: 12,
    right: 12,
    bottom: 12,
    zIndex: 90,
    display: 'grid',
    gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
    gap: '8px',
    padding: '8px',
    borderRadius: '24px',
    border: '1px solid var(--board-border, rgba(148,163,184,0.14))',
    background: 'var(--board-card-bg, linear-gradient(180deg, rgba(8,12,20,0.98) 0%, rgba(6,10,18,0.98) 100%))',
    boxShadow: 'var(--board-card-shadow-strong, 0 24px 70px rgba(2,6,23,0.34))',
    backdropFilter: 'blur(18px)',
  },
  mobileBottomNavButton: {
    ...theme.button,
    ...theme.mutedButton,
    minHeight: '50px',
    padding: '8px',
    borderRadius: '18px',
    display: 'grid',
    placeItems: 'center',
    gap: 0,
    boxShadow: 'none',
    textAlign: 'center',
  },
  mobileBottomNavButtonActive: {
    ...theme.primaryButton,
    boxShadow: 'none',
  },
  mobileBottomNavIcon: {
    width: '26px',
    height: '26px',
    display: 'inline-grid',
    placeItems: 'center',
    flex: '0 0 auto',
    fontSize: '1.18rem',
    lineHeight: 1,
  },
  mobileBottomNavLabel: {
    fontSize: '11px',
    fontWeight: 700,
    lineHeight: 1.1,
  },
  mobilePageFrame: {
    display: 'grid',
    gap: '16px',
    paddingBottom: '92px',
  },
  mobileNoticeCard: {
    ...theme.glassCard,
    padding: '24px',
    display: 'grid',
    gap: '12px',
    color: 'var(--board-text-strong, #f8fafc)',
  },
  mobileNoticeTitle: {
    margin: 0,
    fontSize: '22px',
    fontWeight: 800,
  },
  mobileNoticeText: {
    margin: 0,
    color: 'var(--board-text-soft, rgba(226,232,240,0.74))',
    lineHeight: 1.6,
  },
  drawerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
  },
  closeButton: {
    ...theme.button,
    ...theme.mutedButton,
    width: '40px',
    height: '40px',
    padding: 0,
    display: 'grid',
    placeItems: 'center',
  },
  loginShell: {
    minHeight: '100vh',
    padding: '24px',
    display: 'grid',
    placeItems: 'center',
  },
  loginKicker: {
    ...theme.pageEyebrow,
  },
  loginCard: {
    ...theme.glassCard,
    width: 'min(100%, 360px)',
    padding: '24px',
    color: 'var(--board-text-strong, #f8fafc)',
    display: 'grid',
    gap: '14px',
    alignContent: 'start',
  },
  loginInput: {
    ...theme.input,
    width: '100%',
  },
  loginButton: {
    ...theme.button,
    ...theme.primaryButton,
    width: '100%',
  },
  errorText: {
    color: '#fca5a5',
    fontSize: '13px',
    margin: 0,
  },
  disabledPage: {
    ...theme.page,
    minHeight: '100vh',
    display: 'grid',
    placeItems: 'center',
    padding: '24px',
    boxSizing: 'border-box',
  },
  disabledPanel: {
    ...theme.glassCard,
    maxWidth: '760px',
    padding: '42px',
    color: 'var(--board-text-strong, #f8fafc)',
    textAlign: 'center',
  },
  disabledTitle: {
    margin: 0,
    fontSize: '32px',
    lineHeight: 1.15,
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    display: 'grid',
    placeItems: 'center',
    padding: '18px',
    background: 'var(--board-overlay, rgba(4, 6, 14, 0.72))',
    zIndex: 400,
    backdropFilter: 'blur(10px)',
  },
  modalCard: {
    ...theme.glassCard,
    width: 'min(100%, 480px)',
    padding: '24px',
    color: 'var(--board-text-strong, #f8fafc)',
  },
  modalTitle: {
    margin: '0 0 10px',
    fontSize: '24px',
    color: 'var(--board-text-strong, #f8fafc)',
  },
  modalText: {
    margin: 0,
    color: 'var(--board-text-soft, rgba(226,232,240,0.78))',
    lineHeight: 1.55,
    whiteSpace: 'pre-wrap',
  },
  modalActions: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    marginTop: '20px',
  },
  modalDangerButton: {
    ...theme.button,
    ...theme.dangerButton,
  },
};

function appTitle(boardName) {
  return boardName ? `${APP_NAME} - ${boardName}` : APP_NAME;
}

function formatTodayLabel() {
  return new Date().toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function getPageFromHash() {
  const key = window.location.hash.replace(/^#\/?/, '');
  return pageDefinitions.some((page) => page.key === key) ? key : 'overview';
}

function updatePageHash(key) {
  const hash = `#${key}`;
  if (window.location.hash !== hash) {
    window.history.pushState(null, '', hash);
  }
}

function getThemeStorageKey(username) {
  return `${THEME_STORAGE_PREFIX}:${String(username || 'guest').trim().toLowerCase()}`;
}

function getStoredThemeModeByUsername(username) {
  if (!username) return THEME_MODE_DARK;
  try {
    const stored = window.localStorage.getItem(getThemeStorageKey(username));
    return stored === THEME_MODE_LIGHT ? THEME_MODE_LIGHT : THEME_MODE_DARK;
  } catch {
    return THEME_MODE_DARK;
  }
}

function getStoredThemeModeForUser(user) {
  return getStoredThemeModeByUsername(user?.username);
}

function storeThemeModeForUser(user, mode) {
  if (!user?.username) return;
  try {
    window.localStorage.setItem(
      getThemeStorageKey(user.username),
      mode === THEME_MODE_LIGHT ? THEME_MODE_LIGHT : THEME_MODE_DARK,
    );
  } catch {
    // Theme preference is non-critical.
  }
}

function getSidebarStorageKey(username) {
  return `${SIDEBAR_STORAGE_PREFIX}:${username}`;
}

function getStoredSidebarCollapsedByUsername(username) {
  if (!username) return false;
  try {
    return window.localStorage.getItem(getSidebarStorageKey(username)) === '1';
  } catch {
    return false;
  }
}

function getStoredSidebarCollapsedForUser(user) {
  return getStoredSidebarCollapsedByUsername(user?.username);
}

function storeSidebarCollapsedForUser(user, collapsed) {
  if (!user?.username) return;
  try {
    window.localStorage.setItem(getSidebarStorageKey(user.username), collapsed ? '1' : '0');
  } catch {
    // Sidebar preference is non-critical.
  }
}

function setDocumentBranding(logoUrl, boardName = '') {
  document.title = appTitle(boardName);

  const favicon = document.querySelector("link[rel='icon']");
  if (favicon) {
    favicon.href = logoUrl || DEFAULT_FAVICON;
  }

  const appleIcon = document.querySelector("link[rel='apple-touch-icon']");
  if (appleIcon && logoUrl) {
    appleIcon.href = logoUrl;
  }
}

function hasPagePermission(user, pageKey) {
  if (!user) return false;
  if (user.permissions?.adminUser) return true;
  if (pageKey === 'overview') return true;
  if (pageKey === 'jobs' || pageKey === 'areas') return Boolean(user.permissions?.planner);
  if (pageKey === 'loadingBayPlanner') {
    return Boolean(user.permissions?.loadingBayPlanner || user.permissions?.planner);
  }
  if (pageKey === 'loadingBays') {
    return Boolean(user.permissions?.loadingBays || user.permissions?.gates);
  }
  if (pageKey === 'warehouseLookup') {
    return Boolean(user.permissions?.warehouseLookup || user.permissions?.layoutEditor);
  }
  return Boolean(user.permissions?.[pageKey]);
}

function isPageFeatureAvailable(settings, pageKey) {
  if (pageKey === 'drivers') return settings.drivers_enabled !== false;
  if (pageKey === 'vehicles') return settings.vehicles_enabled !== false;
  if (pageKey === 'vehicleClasses') return settings.vehicle_classes_enabled !== false;
  if (pageKey === 'gates') return settings.gates_enabled !== false;
  if (pageKey === 'loadingBays') return settings.loading_bays_enabled !== false;
  if (pageKey === 'planner') return settings.gates_enabled !== false && settings.gate_management_enabled !== false;
  if (pageKey === 'loadingBayPlanner') return settings.loading_bays_enabled !== false && settings.loading_bay_management_enabled !== false;
  if (pageKey === 'jobs' || pageKey === 'areas') return settings.gates_enabled !== false && settings.gate_management_enabled !== false;
  if (pageKey === 'layoutEditor') return settings.warehouse_layout_enabled !== false;
  if (pageKey === 'warehouseLookup') return settings.warehouse_lookup_enabled !== false;
  return true;
}

function isPageRestrictedOnMobile(pageKey, currentUser) {
  if (currentUser?.bootstrap && pageKey === 'websiteUsers') return false;
  return MOBILE_RESTRICTED_PAGE_KEYS.has(pageKey);
}

function BrandLockup({ logoUrl, boardName, collapsed = false }) {
  return (
    <div style={mergeStyles(styles.brand, collapsed && styles.brandCollapsed)} title={boardName || 'Board Instanz'}>
      {logoUrl ? (
        <img style={styles.brandLogo} src={logoUrl} alt="" />
      ) : (
        <div style={styles.brandFallback}>DB</div>
      )}
      <div style={mergeStyles(styles.brandCopy, collapsed && styles.brandCopyCollapsed)}>
        <span style={styles.brandTitle}>{APP_NAME}</span>
        <span style={styles.brandBoard}>{boardName || 'Board Instanz'}</span>
      </div>
    </div>
  );
}

function LoginScreen({ logoUrl, boardName, accent1, accent2, themeMode, whiteAccent, onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const requestLogin = async (payload) => {
    const response = await fetch(`${API_BASE}/website-users/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const loginError = new Error(data.error || 'Login fehlgeschlagen');
      loginError.code = data.code;
      loginError.canTakeover = data.canTakeover === true;
      throw loginError;
    }
    return data;
  };

  const submit = async (event) => {
    event.preventDefault();
    setError('');

    try {
      const user = await requestLogin({ username, password });
      if (!user.token) throw new Error('Kein Token erhalten');
      onLogin(user);
    } catch (err) {
      if (err?.code === 'ACCOUNT_IN_USE' && err?.canTakeover) {
        const confirmed = await showAppConfirm(
          err.message || 'Dieses Konto wird bereits an anderer Stelle verwendet.',
          {
            title: 'Konto bereits aktiv',
            confirmLabel: 'Andere Session trennen',
            cancelLabel: 'Abbrechen',
            danger: true,
          },
        );
        if (!confirmed) return;
        try {
          const user = await requestLogin({ username, password, takeover: true });
          if (!user.token) throw new Error('Kein Token erhalten');
          onLogin(user);
          return;
        } catch (takeoverError) {
          setError(takeoverError.message || 'Login fehlgeschlagen');
          return;
        }
      }
      setError(err.message || 'Login fehlgeschlagen');
    }
  };

  return (
    <div style={brandedPageStyle(themeMode, accent1, accent2, whiteAccent)}>
      <div style={styles.loginShell}>
        <form style={styles.loginCard} onSubmit={submit}>
          <div style={{ display: 'grid', gap: '10px', justifyItems: 'start' }}>
            <BrandLockup logoUrl={logoUrl} boardName={boardName} />
            <p style={styles.loginKicker}>Anmeldung</p>
          </div>
          <input
            id="username"
            name="username"
            style={styles.loginInput}
            placeholder="Username"
            type="text"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            autoComplete="username"
          />
          <input
            id="password"
            name="password"
            style={styles.loginInput}
            placeholder="Passwort"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
          />
          <button style={styles.loginButton} type="submit">Einloggen</button>
          {error && <p style={styles.errorText}>{error}</p>}
        </form>
      </div>
    </div>
  );
}

function DisplayLicenseInactive() {
  return (
    <main style={styles.displayLicenseInactive}>
      <strong>DISPOBOARD.EU</strong>
      <span>Lizenz nicht aktiv</span>
    </main>
  );
}

function DisplayLoading() {
  return <main style={styles.displayLicenseInactive} aria-hidden="true" />;
}

function FlaticonIcon({ icon, fallback, style }) {
  return (
    <span style={style || styles.navShort} title={fallback} aria-hidden="true">
      <i className={icon} style={styles.navIconGlyph} />
    </span>
  );
}

function ThemeModeIcon({ mode }) {
  if (mode === THEME_MODE_LIGHT) {
    return <FlaticonIcon icon="fi fi-rr-brightness" fallback="Light" style={{ ...styles.footerIcon, fontSize: '14px' }} />;
  }
  return <FlaticonIcon icon="fi fi-rr-moon-stars" fallback="Dark" style={{ ...styles.footerIcon, fontSize: '14px' }} />;
}

function SidebarCollapseIcon({ collapsed }) {
  return (
    <FlaticonIcon
      icon={collapsed ? 'fi fi-rr-angle-small-right' : 'fi fi-rr-angle-small-left'}
      fallback={collapsed ? 'Open' : 'Close'}
      style={{ ...styles.footerIcon, fontSize: '16px' }}
    />
  );
}

function SidebarNavigation({
  logoUrl,
  boardName,
  sections,
  currentPage,
  onNavigate,
  onHelp,
  onLogout,
  isDrawer = false,
  collapsed = false,
  onToggleCollapsed = null,
}) {
  const [hoveredItem, setHoveredItem] = useState(null);
  return (
    <>
      <div style={styles.sidebarTop}>
        <div style={mergeStyles(styles.sidebarTopRow, collapsed && styles.sidebarTopRowCollapsed)}>
          <BrandLockup logoUrl={logoUrl} boardName={boardName} collapsed={collapsed} />
          {!isDrawer && typeof onToggleCollapsed === 'function' && (
            <button
              type="button"
              aria-label={collapsed ? 'Sidebar ausklappen' : 'Sidebar einklappen'}
              title={collapsed ? 'Sidebar ausklappen' : 'Sidebar einklappen'}
              style={styles.sidebarToggleButton}
              onClick={onToggleCollapsed}
            >
              <SidebarCollapseIcon collapsed={collapsed} />
            </button>
          )}
        </div>
      </div>
      <div style={styles.sidebarDivider} />
      <div style={styles.navScroll}>
        {sections.map((section) => (
          <div key={section.title} style={mergeStyles(styles.navGroup, collapsed && styles.navGroupCollapsed)}>
            <p style={mergeStyles(styles.navGroupTitle, collapsed && styles.navGroupTitleCollapsed)}>{section.title}</p>
            {section.pages.map((page) => (
              <button
                key={page.key}
                type="button"
                aria-label={page.label}
                title={page.label}
                style={mergeStyles(
                  styles.navButton,
                  collapsed && styles.navButtonCollapsed,
                  hoveredItem === page.key && styles.navButtonHover,
                  currentPage === page.key && styles.navButtonActive,
                )}
                onMouseEnter={() => setHoveredItem(page.key)}
                onMouseLeave={() => setHoveredItem(null)}
                onFocus={() => setHoveredItem(page.key)}
                onBlur={() => setHoveredItem(null)}
                onClick={() => onNavigate(page.key)}
              >
                <FlaticonIcon icon={page.icon} fallback={page.short} />
                <span style={mergeStyles(styles.navText, collapsed && styles.navTextCollapsed)}>
                  <span style={styles.navLabel}>{page.label}</span>
                  <span style={styles.navHint}>{page.title}</span>
                </span>
              </button>
            ))}
          </div>
        ))}
      </div>
      <div style={styles.sidebarDivider} />
      <div style={styles.sidebarFooter}>
        <button
          type="button"
          aria-label="Hilfe"
          title="Hilfe"
          style={mergeStyles(styles.footerButton, collapsed && styles.footerButtonCollapsed, hoveredItem === 'help' && styles.navButtonHover)}
          onMouseEnter={() => setHoveredItem('help')}
          onMouseLeave={() => setHoveredItem(null)}
          onFocus={() => setHoveredItem('help')}
          onBlur={() => setHoveredItem(null)}
          onClick={onHelp}
        >
          <FlaticonIcon icon="fi fi-rr-interrogation" fallback="Hilfe" style={styles.footerIcon} />
          {!collapsed && 'Hilfe'}
        </button>
        <button
          type="button"
          aria-label="Abmelden"
          title="Abmelden"
          style={mergeStyles(
            isDrawer ? styles.footerButton : styles.footerDangerButton,
            collapsed && (isDrawer ? styles.footerButtonCollapsed : styles.footerDangerButtonCollapsed),
            hoveredItem === 'logout' && styles.navButtonHover,
          )}
          onMouseEnter={() => setHoveredItem('logout')}
          onMouseLeave={() => setHoveredItem(null)}
          onFocus={() => setHoveredItem('logout')}
          onBlur={() => setHoveredItem(null)}
          onClick={onLogout}
        >
          <FlaticonIcon icon="fi fi-rr-sign-out-alt" fallback="Abmelden" style={styles.footerIcon} />
          {!collapsed && 'Abmelden'}
        </button>
      </div>
    </>
  );
}

function OverviewPanel() {
  return <LayoutViewer title="Aktuelle Tor-Zuweisung" />;
}

function MobileUnavailablePanel({ page }) {
  return (
    <section style={styles.mobileNoticeCard}>
      <h2 style={styles.mobileNoticeTitle}>{page?.label || 'Seite nicht verfÃ¼gbar'}</h2>
      <p style={styles.mobileNoticeText}>
        Diese Ansicht ist auf Handy und Tablet bewusst ausgeblendet. Nutze fÃ¼r Lagereditor und andere Drag-Workflows bitte die Desktop-Ansicht.
      </p>
    </section>
  );
}

function App() {
  const currentPath = window.location.pathname;
  const isDisplayPath = currentPath.startsWith('/display');
  const [page, setPage] = useState(() => getPageFromHash());
  const [, setJobs] = useState([]);
  const [settings, setSettings] = useState({
    board_name: '',
    site_logo_url: '',
    drivers_enabled: false,
    vehicles_enabled: false,
    vehicle_classes_enabled: false,
    gates_enabled: false,
    loading_bays_enabled: false,
    gate_management_enabled: false,
    loading_bay_management_enabled: false,
    warehouse_layout_enabled: true,
    warehouse_lookup_enabled: true,
    theme_accent_1: '',
    theme_accent_2: '',
    default_white_mode_accent: '',
    license_preset_name: 'CUSTOM',
    instance_disabled: false,
  });
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(() => getStoredUser());
  const [themeMode, setThemeMode] = useState(() => getStoredThemeModeForUser(getStoredUser()));
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => getStoredSidebarCollapsedForUser(getStoredUser()));
  const [sessionBlocked, setSessionBlocked] = useState(false);
  const [sessionBlockedMessage, setSessionBlockedMessage] = useState('Dieses Konto wird aktuell in einer anderen Session verwendet.');
  const [sessionSwitchBusy, setSessionSwitchBusy] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const [modalState, setModalState] = useState(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  useEffect(() => {
    setDocumentBranding('', '');
    fetch(`${API_BASE}/public/settings`)
      .then((response) => response.json())
      .then((data) => {
        const nextSettings = {
          board_name: data.board_name || '',
          site_logo_url: data.site_logo_url || '',
          drivers_enabled: data.drivers_enabled !== false,
          vehicles_enabled: data.vehicles_enabled !== false,
          vehicle_classes_enabled: data.vehicle_classes_enabled !== false,
          gates_enabled: data.gates_enabled !== false,
          loading_bays_enabled: data.loading_bays_enabled !== false,
          gate_management_enabled: data.gate_management_enabled !== false,
          loading_bay_management_enabled: data.loading_bay_management_enabled !== false,
          warehouse_layout_enabled: data.warehouse_layout_enabled !== false,
          warehouse_lookup_enabled: data.warehouse_lookup_enabled !== false,
          theme_accent_1: data.theme_accent_1 || '',
          theme_accent_2: data.theme_accent_2 || '',
          default_white_mode_accent: data.default_white_mode_accent || '',
          license_preset_name: data.license_preset_name || 'CUSTOM',
          instance_disabled: data.instance_disabled === true,
        };
        setSettings(nextSettings);
        setDocumentBranding(nextSettings.site_logo_url, nextSettings.board_name);
        setSettingsLoaded(true);
      })
      .catch(() => {
        setDocumentBranding('', '');
        setSettings((currentSettings) => ({
          ...currentSettings,
          gates_enabled: false,
          loading_bays_enabled: false,
          gate_management_enabled: false,
          loading_bay_management_enabled: false,
        }));
        setSettingsLoaded(true);
      });
  }, []);

  useEffect(() => {
    applyThemeVariables(
      themeMode,
      settings.theme_accent_1,
      settings.theme_accent_2,
      settings.default_white_mode_accent,
    );
  }, [themeMode, settings.theme_accent_1, settings.theme_accent_2, settings.default_white_mode_accent]);

  useEffect(() => {
    const username = currentUser?.username;
    if (username) {
      setThemeMode(getStoredThemeModeByUsername(username));
      setSidebarCollapsed(getStoredSidebarCollapsedByUsername(username));
    } else {
      setSidebarCollapsed(false);
    }
  }, [currentUser?.username]);

  useEffect(() => {
    const onSettingsUpdated = (event) => {
      setSettings((currentSettings) => {
        const nextSettings = {
          ...currentSettings,
          board_name: event.detail?.board_name || currentSettings.board_name,
          site_logo_url: event.detail?.site_logo_url || '',
          theme_accent_1: event.detail?.theme_accent_1 || currentSettings.theme_accent_1,
          theme_accent_2: event.detail?.theme_accent_2 || currentSettings.theme_accent_2,
          default_white_mode_accent: event.detail?.default_white_mode_accent || currentSettings.default_white_mode_accent,
          license_preset_name: event.detail?.license_preset_name || currentSettings.license_preset_name,
        };
        setDocumentBranding(nextSettings.site_logo_url, nextSettings.board_name);
        return nextSettings;
      });
    };

    window.addEventListener('dispoboard:settings-updated', onSettingsUpdated);
    return () => window.removeEventListener('dispoboard:settings-updated', onSettingsUpdated);
  }, []);

  useEffect(() => {
    const onUnauthorized = () => {
      clearStoredUser();
      setCurrentUser(null);
      setSessionBlocked(false);
      setUserMenuOpen(false);
      setMenuOpen(false);
      setPage('overview');
      updatePageHash('overview');
    };

    window.addEventListener('torverwaltung:unauthorized', onUnauthorized);
    return () => window.removeEventListener('torverwaltung:unauthorized', onUnauthorized);
  }, []);

  useEffect(() => {
    const onSessionConflict = (event) => {
      const nextMessage = event.detail?.message || 'Dieses Konto wird aktuell in einer anderen Session verwendet.';
      setSessionBlocked(true);
      setSessionBlockedMessage(nextMessage);
      setUserMenuOpen(false);
      setMenuOpen(false);
      setModalState({
        type: 'session-blocked',
        title: 'Session vorübergehend gesperrt',
        message: nextMessage,
        confirmLabel: 'Andere Session übernehmen',
        cancelLabel: 'Abbrechen',
        danger: true,
      });
    };

    window.addEventListener('torverwaltung:session-conflict', onSessionConflict);
    return () => window.removeEventListener('torverwaltung:session-conflict', onSessionConflict);
  }, []);

  useEffect(() => {
    const onModal = (event) => {
      setModalState(event.detail || null);
    };

    window.addEventListener('dispoboard:modal', onModal);
    return () => window.removeEventListener('dispoboard:modal', onModal);
  }, []);

  useEffect(() => {
    const onHashChange = () => setPage(getPageFromHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  useEffect(() => {
    if (!currentUser || currentUser.bootstrap) return;
    apiFetch(`${API_BASE}/jobs`)
      .then((response) => response.json())
      .then((data) => setJobs(Array.isArray(data) ? data : []))
      .catch(() => setJobs([]));
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser?.token || sessionBlocked) return undefined;

    let stopped = false;
    const sendHeartbeat = async () => {
      if (stopped) return;
      try {
        await apiFetch(`${API_BASE}/website-users/session/heartbeat`, { method: 'POST' });
      } catch {
        // apiFetch handles unauthorized/session-conflict state globally.
      }
    };

    sendHeartbeat();
    const intervalId = window.setInterval(sendHeartbeat, 30000);
    const onFocus = () => sendHeartbeat();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') sendHeartbeat();
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      stopped = true;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [currentUser?.token, sessionBlocked]);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Individual displays own loading, license errors and freshness through their snapshot API.
  if (isDisplayPath && currentPath !== '/display/overview') return <GateDisplay />;

  if (isDisplayPath && !settingsLoaded) {
    return <DisplayLoading />;
  }

  if (currentPath === '/display/overview' && (settings.warehouse_layout_enabled === false || !((settings.gates_enabled !== false && settings.gate_management_enabled !== false) || (settings.loading_bays_enabled !== false && settings.loading_bay_management_enabled !== false)))) {
    return <DisplayLicenseInactive />;
  }

  if (currentPath === '/display/overview') {
    return <LayoutViewer fullScreen />;
  }

  const navigate = (key) => {
    if (currentUser?.bootstrap && key !== 'websiteUsers') {
      setModalState({
        type: 'alert',
        title: 'Admin-User erforderlich',
        message: 'Du musst zuerst einen Admin-User anlegen, bevor weitere Daten sichtbar oder bearbeitbar sind.',
        confirmLabel: 'Verstanden',
      });
      return;
    }

    setPage(key);
    updatePageHash(key);
    setMenuOpen(false);
    setUserMenuOpen(false);
  };

  const handleLogin = (user) => {
    const normalizedUser = {
      ...user,
      permissions: user.permissions || { overview: true },
    };
    storeUser(normalizedUser);
    setThemeMode(getStoredThemeModeForUser(normalizedUser));
    setSessionBlocked(false);
    setCurrentUser(normalizedUser);
    const nextPage = normalizedUser.bootstrap ? 'websiteUsers' : getPageFromHash();
    setPage(nextPage);
    updatePageHash(nextPage);
  };

  const changeThemeMode = (nextMode) => {
    const normalizedMode = nextMode === THEME_MODE_LIGHT ? THEME_MODE_LIGHT : THEME_MODE_DARK;
    setThemeMode(normalizedMode);
    storeThemeModeForUser(currentUser, normalizedMode);
    setUserMenuOpen(false);
  };

  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed((value) => {
      const nextValue = !value;
      storeSidebarCollapsedForUser(currentUser, nextValue);
      return nextValue;
    });
  };

  const logout = async () => {
    try {
      await apiFetch(`${API_BASE}/website-users/logout`, { method: 'POST' });
    } catch {
      // Local logout must still complete if the server session is already gone.
    } finally {
      clearStoredUser();
      setCurrentUser(null);
      setSessionBlocked(false);
      setUserMenuOpen(false);
      setMenuOpen(false);
      setPage('overview');
      updatePageHash('overview');
    }
  };

  const closeModal = (result = false) => {
    if (typeof modalState?.resolve === 'function') {
      modalState.resolve(result);
    }
    setModalState(null);
  };

  const reclaimBlockedSession = async () => {
    if (!currentUser?.token || sessionSwitchBusy) return;
    setSessionSwitchBusy(true);
    try {
      const response = await fetch(`${API_BASE}/website-users/session/reclaim`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${currentUser.token}`,
        },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const reclaimError = new Error(data.error || 'Session konnte nicht übernommen werden.');
        reclaimError.code = data.code;
        reclaimError.retryAfterSeconds = data.retryAfterSeconds;
        throw reclaimError;
      }
      setSessionBlocked(false);
      setSessionBlockedMessage('Dieses Konto wird aktuell in einer anderen Session verwendet.');
      setModalState(null);
    } catch (err) {
      if (err?.code === 'SESSION_SWITCH_RATE_LIMIT') {
        const nextMessage = err.message || 'Session-Wechsel ist aktuell noch gesperrt.';
        setSessionBlockedMessage(nextMessage);
        setModalState({
          type: 'session-blocked',
          title: 'Session-Wechsel begrenzt',
          message: nextMessage,
          confirmLabel: 'Andere Session übernehmen',
          cancelLabel: 'Abbrechen',
          danger: true,
        });
        return;
      }
      if (err?.message === 'Nicht eingeloggt') {
        clearStoredUser();
        setCurrentUser(null);
        setSessionBlocked(false);
        setModalState(null);
        return;
      }
      setModalState({
        type: 'alert',
        title: 'Übernahme fehlgeschlagen',
        message: err.message || 'Session konnte nicht übernommen werden.',
        confirmLabel: 'Verstanden',
        danger: true,
      });
    } finally {
      setSessionSwitchBusy(false);
    }
  };

  const modalOverlay = modalState && (
    <div style={styles.modalOverlay} onClick={() => closeModal(false)}>
      <div style={styles.modalCard} onClick={(event) => event.stopPropagation()}>
        <h2 style={styles.modalTitle}>{modalState.title || 'Hinweis'}</h2>
        <p style={styles.modalText}>{modalState.message || ''}</p>
        <div style={styles.modalActions}>
          {(modalState.type === 'confirm' || modalState.type === 'session-blocked') && (
            <button type="button" style={theme.button} onClick={() => closeModal(false)}>
              {modalState.cancelLabel || 'Abbrechen'}
            </button>
          )}
          {modalState.type === 'session-blocked' ? (
            <button
              type="button"
              style={styles.modalDangerButton}
              onClick={reclaimBlockedSession}
              disabled={sessionSwitchBusy}
            >
              {sessionSwitchBusy ? 'Übernehme...' : (modalState.confirmLabel || 'Übernehmen')}
            </button>
          ) : (
          <button
            type="button"
            style={modalState.danger ? styles.modalDangerButton : mergeStyles(theme.button, theme.primaryButton)}
            onClick={() => closeModal(true)}
          >
            {modalState.confirmLabel || 'OK'}
          </button>
          )}
        </div>
      </div>
    </div>
  );

  const currentPageMeta = pageDefinitions.find((item) => item.key === page) || pageDefinitions[0];
  const isMobileShell = viewportWidth < 1180;
  const isPhone = viewportWidth < 760;

  const visibleSidebarSections = sidebarSections
    .map((section) => ({
      ...section,
      pages: section.pages
        .map((pageKey) => pageDefinitions.find((item) => item.key === pageKey))
        .filter((item) => item && (
          currentUser?.bootstrap
            ? item.key === 'websiteUsers'
            : hasPagePermission(currentUser, item.key)
              && isPageFeatureAvailable(settings, item.key)
              && (!isMobileShell || !isPageRestrictedOnMobile(item.key, currentUser))
        )),
    }))
    .filter((section) => section.pages.length > 0);

  const mobileTabPages = MOBILE_TAB_PAGE_KEYS
    .map((pageKey) => pageDefinitions.find((item) => item.key === pageKey))
    .filter((item) => item && hasPagePermission(currentUser, item.key) && isPageFeatureAvailable(settings, item.key));

  const mobilePageRestricted = isMobileShell && isPageRestrictedOnMobile(page, currentUser);
  const morePagesAvailable = visibleSidebarSections.some((section) => (
    section.pages.some((item) => !MOBILE_TAB_PAGE_KEYS.includes(item.key))
  ));

  const canViewPage = currentUser?.bootstrap
    ? page === 'websiteUsers'
    : (hasPagePermission(currentUser, page) && isPageFeatureAvailable(settings, page));

  const currentUserRole = currentUser?.bootstrap
    ? 'Setup'
    : currentUser?.permissions?.adminUser
      ? 'Admin'
      : 'Board User';

  const renderPage = () => {
    if (!canViewPage) {
      return <div style={styles.deniedCard}>Fehlende Berechtigung</div>;
    }

    if (mobilePageRestricted) {
      return <MobileUnavailablePanel page={currentPageMeta} />;
    }

    switch (page) {
      case 'overview':
        return <OverviewPanel />;
      case 'planner':
      case 'loadingBayPlanner':
        return <JobPlanner />;
      case 'jobs':
        return <Jobs />;
      case 'license':
        return <LicenseView />;
      case 'areas':
        return <PlanningAreas />;
      case 'gates':
        return <Gates />;
      case 'loadingBays':
        return <LoadingBays />;
      case 'vehicles':
        return <Vehicles />;
      case 'drivers':
        return <Drivers />;
      case 'vehicleClasses':
        return <VehicleClasses />;
      case 'layoutEditor':
        return <LayoutEditor />;
      case 'displayLayout':
        return <DisplayLayoutEditor />;
      case 'warehouseLookup':
        return <WarehouseLookup />;
      case 'general':
        return <GeneralSettings />;
      case 'websiteUsers':
        return <WebsiteUsers currentUser={currentUser} />;
      default:
        return <div style={styles.deniedCard}>Seite nicht gefunden</div>;
    }
  };

  const showHelp = () => {
    setModalState({
      type: 'alert',
      title: 'Hilfe',
      message: 'FÃ¼r RÃ¼ckfragen zu Board, Berechtigungen oder Setup nutze die Management-Instanz oder den internen Support-Kanal.',
      confirmLabel: 'SchlieÃŸen',
    });
  };

  if (!currentUser) {
    return (
      <>
        <LoginScreen
          logoUrl={settings.site_logo_url}
          boardName={settings.board_name}
          accent1={settings.theme_accent_1}
          accent2={settings.theme_accent_2}
          themeMode={themeMode}
          whiteAccent={settings.default_white_mode_accent}
          onLogin={handleLogin}
        />
        {modalOverlay}
      </>
    );
  }

  if (currentUser.instanceDisabled || settings.instance_disabled) {
    return (
      <>
        <div style={brandedPageStyle(themeMode, settings.theme_accent_1, settings.theme_accent_2, settings.default_white_mode_accent)}>
          <main style={styles.disabledPage}>
            <section style={styles.disabledPanel}>
              <h1 style={styles.disabledTitle}>Ihr Zugang zum DispoBoard ist aktuell deaktiviert</h1>
            </section>
          </main>
        </div>
        {modalOverlay}
      </>
    );
  }

  return (
    <div style={brandedPageStyle(themeMode, settings.theme_accent_1, settings.theme_accent_2, settings.default_white_mode_accent)}>
      <div style={styles.shell}>
        {menuOpen && (
          <>
            <div style={styles.overlay} onClick={() => setMenuOpen(false)} />
            <aside style={styles.drawer}>
              <div style={styles.drawerHeader}>
                <strong>Navigation</strong>
                <button type="button" style={styles.closeButton} onClick={() => setMenuOpen(false)}>X</button>
              </div>
              <SidebarNavigation
                logoUrl={settings.site_logo_url}
                boardName={settings.board_name}
                sections={visibleSidebarSections}
                currentPage={page}
                onNavigate={navigate}
                onHelp={showHelp}
                onLogout={logout}
                isDrawer
              />
            </aside>
          </>
        )}

        <div
          style={mergeStyles(
            styles.appGrid,
            isMobileShell
              ? { gridTemplateColumns: '1fr' }
              : { gridTemplateColumns: `${sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH}px minmax(0, 1fr)` },
          )}
        >
          {!isMobileShell && (
            <aside style={mergeStyles(styles.sidebar, sidebarCollapsed && styles.sidebarCollapsed)}>
              <SidebarNavigation
                logoUrl={settings.site_logo_url}
                boardName={settings.board_name}
                sections={visibleSidebarSections}
                currentPage={page}
                onNavigate={navigate}
                onHelp={showHelp}
                onLogout={logout}
                collapsed={sidebarCollapsed}
                onToggleCollapsed={toggleSidebarCollapsed}
              />
            </aside>
          )}

          <div style={mergeStyles(styles.main, isMobileShell && styles.mobilePageFrame)}>
            {isMobileShell ? (
              <header style={styles.mobileTopbar}>
                <button type="button" style={styles.mobileMenuButton} onClick={() => setMenuOpen(true)}>â˜°</button>
                <h1 style={styles.mobileTopbarTitle}>{currentPageMeta.label}</h1>
                <div style={mergeStyles(styles.themeToggle, isPhone && { justifySelf: 'end' })}>
                  <button
                    type="button"
                    aria-label="Dark Mode"
                    title="Dark Mode"
                    style={mergeStyles(styles.themeToggleButton, isPhone && { minWidth: '50px', padding: '7px 8px' }, themeMode === THEME_MODE_DARK && styles.themeToggleButtonActive)}
                    onClick={() => changeThemeMode(THEME_MODE_DARK)}
                  >
                    <ThemeModeIcon mode={THEME_MODE_DARK} />
                  </button>
                  <button
                    type="button"
                    aria-label="Light Mode"
                    title="Light Mode"
                    style={mergeStyles(styles.themeToggleButton, isPhone && { minWidth: '50px', padding: '7px 8px' }, themeMode === THEME_MODE_LIGHT && styles.themeToggleButtonActive)}
                    onClick={() => changeThemeMode(THEME_MODE_LIGHT)}
                  >
                    <ThemeModeIcon mode={THEME_MODE_LIGHT} />
                  </button>
                </div>
              </header>
            ) : (
              <header style={styles.topbar}>
                <div style={styles.topbarLeft}>
                  <p style={styles.topbarEyebrow}>{currentPageMeta.section}</p>
                  <h1 style={styles.topbarTitle}>{currentPageMeta.label}</h1>
                </div>
                <div style={styles.topbarRight}>
                  <div style={styles.topbarChip}>
                    <span style={styles.chipLabel}>Heute</span>
                    <span style={styles.chipValue}>{formatTodayLabel()}</span>
                  </div>
                  <div style={styles.topbarChip}>
                    <span style={styles.chipLabel}>Board</span>
                    <span style={styles.chipValue}>{settings.board_name || 'Instanz'}</span>
                  </div>
                  <div style={styles.themeToggle}>
                    <button
                      type="button"
                      aria-label="Dark Mode"
                      title="Dark Mode"
                      style={mergeStyles(styles.themeToggleButton, themeMode === THEME_MODE_DARK && styles.themeToggleButtonActive)}
                      onClick={() => changeThemeMode(THEME_MODE_DARK)}
                    >
                      <ThemeModeIcon mode={THEME_MODE_DARK} />
                    </button>
                    <button
                      type="button"
                      aria-label="Light Mode"
                      title="Light Mode"
                      style={mergeStyles(styles.themeToggleButton, themeMode === THEME_MODE_LIGHT && styles.themeToggleButtonActive)}
                      onClick={() => changeThemeMode(THEME_MODE_LIGHT)}
                    >
                      <ThemeModeIcon mode={THEME_MODE_LIGHT} />
                    </button>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <button type="button" style={styles.userButton} onClick={() => setUserMenuOpen((open) => !open)}>
                      <span style={styles.userAvatar}>{String(currentUser.username || 'U').slice(0, 1)}</span>
                      <span style={styles.userCopy}>
                        <span style={styles.userName}>{currentUser.username}</span>
                        <span style={styles.userRole}>{currentUserRole}</span>
                      </span>
                    </button>
                    {userMenuOpen && (
                      <div style={styles.userMenu}>
                        <button type="button" style={styles.userMenuButton} onClick={showHelp}>Hilfe</button>
                        <button type="button" style={styles.userMenuButton} onClick={logout}>Abmelden</button>
                      </div>
                    )}
                  </div>
                </div>
              </header>
            )}

            <main style={mergeStyles(styles.content, isMobileShell && { gap: '16px' })}>
              {sessionBlocked ? (
                <section style={styles.sessionBlockedCard}>
                  <h2 style={styles.sessionBlockedTitle}>Session temporär gesperrt</h2>
                  <p style={styles.sessionBlockedText}>
                    {sessionBlockedMessage}
                  </p>
                  <button
                    type="button"
                    style={mergeStyles(theme.button, theme.primaryButton)}
                    onClick={reclaimBlockedSession}
                    disabled={sessionSwitchBusy}
                  >
                    {sessionSwitchBusy ? 'Übernehme...' : 'Andere Session wieder übernehmen'}
                  </button>
                </section>
              ) : (
                renderPage()
              )}
            </main>
          </div>
        </div>

        {isMobileShell && (
          <nav
            style={mergeStyles(styles.mobileBottomNav, {
              gridTemplateColumns: `repeat(${Math.max(2, mobileTabPages.length + 1)}, minmax(0, 1fr))`,
            })}
            aria-label="Mobile Navigation"
          >
            {mobileTabPages.map((item) => (
              <button
                key={item.key}
                type="button"
                aria-label={item.label}
                style={mergeStyles(
                  styles.mobileBottomNavButton,
                  page === item.key && styles.mobileBottomNavButtonActive,
                )}
                onClick={() => navigate(item.key)}
              >
                <FlaticonIcon icon={item.icon} fallback={item.short} style={styles.mobileBottomNavIcon} />
              </button>
            ))}
            <button
              type="button"
              aria-label="Sonstiges"
              style={mergeStyles(
                styles.mobileBottomNavButton,
                ((!MOBILE_TAB_PAGE_KEYS.includes(page) && morePagesAvailable) || menuOpen) && styles.mobileBottomNavButtonActive,
              )}
              onClick={() => setMenuOpen(true)}
            >
              <FlaticonIcon icon="fi fi-rr-menu-dots" fallback="Sonstiges" style={styles.mobileBottomNavIcon} />
            </button>
          </nav>
        )}
      </div>

      {modalOverlay}
    </div>
  );
}

export default App;

